import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import {
  DeterministicMealSuggestionAdapter,
  StaticHorizonCatalog,
  UnavailableArchibaldHandoff,
  UnavailableRecipeReturnVerifier,
} from './application/adapters';
import { InventoryService } from './application/inventory-service';
import { applySearchIndexingPolicy, currentAppRuntime } from './config/runtime';
import { HORIZON_RULES } from './content/catalog';
import { IndexedDbInventoryRepository } from './infrastructure/db';
import { ProductionArchibaldHandoff } from './infrastructure/archibald-handoff';
import { DisabledMonetizationAdapter } from './infrastructure/monetization';
import { LocalHouseholdArtifacts } from './infrastructure/household-artifacts';
import { federatedBootstrapRequested, federatedInventoryDatabase, restoreFederatedInventory } from './infrastructure/federated-bootstrap';
import {
  ProductionHouseholdSharing,
  UnavailableHouseholdSharing,
} from './infrastructure/household-sharing';
import { I18nProvider } from './i18n/I18nContext';
import { App } from './ui/App';
import { AppProvider } from './ui/AppContext';
import { LocalizedDocumentChrome } from './ui/LocalizedDocumentChrome';
import { FederatedBootstrapFailure } from './ui/FederatedBootstrapFailure';
import './ui/styles.css';

const runtime = currentAppRuntime();
applySearchIndexingPolicy(runtime);
const root = document.querySelector<HTMLDivElement>('#root');
if (root == null) throw new Error('Point de montage introuvable.');
const reactRoot = createRoot(root);

const householdRuntimeEnabled = import.meta.env.VITE_DOHM_HOUSEHOLD_PUBLIC === '1' &&
  runtime.mode === 'app' &&
  (Capacitor.isNativePlatform() ||
    (window.location.protocol === 'https:' && window.location.hostname !== 'localhost') ||
    window.location.hostname === '127.0.0.1');
const sharing = householdRuntimeEnabled
  ? new ProductionHouseholdSharing(runtime.databaseName)
  : new UnavailableHouseholdSharing(runtime.mode === 'demo' ? 'demo' : 'not-configured');

const mount = async () => {
const handoffRuntimeEnabled = import.meta.env.VITE_DOHM_HANDOFF_PUBLIC === '1' &&
  runtime.mode === 'app' &&
  ((window.location.protocol === 'https:' && window.location.hostname !== 'localhost') ||
    window.location.hostname === '127.0.0.1');
const federated = runtime.mode === 'app' &&
  federatedBootstrapRequested(new URL(window.location.href), window.localStorage);
const account = federated
  ? await federatedInventoryDatabase(runtime.databaseName, sharing, window.localStorage,
    new URL(window.location.href).searchParams.get('my-dohm-return') === '1')
  : null;
const repository = new IndexedDbInventoryRepository(account?.databaseName ?? runtime.databaseName);
const service = new InventoryService(
  repository,
  new StaticHorizonCatalog(HORIZON_RULES),
  new DeterministicMealSuggestionAdapter(),
  new LocalHouseholdArtifacts(),
  new UnavailableRecipeReturnVerifier(),
  handoffRuntimeEnabled
    ? new ProductionArchibaldHandoff()
    : new UnavailableArchibaldHandoff(),
);
const monetization = new DisabledMonetizationAdapter();
if (account != null) {
  await restoreFederatedInventory(service, sharing, account.hasHousehold);
  const url = new URL(window.location.href);
  url.searchParams.delete('my-dohm-return');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

reactRoot.render(
  <StrictMode>
    <I18nProvider>
      <LocalizedDocumentChrome />
      <AppProvider service={service} sharing={sharing} monetization={monetization} runtime={runtime}>
        <App />
      </AppProvider>
    </I18nProvider>
  </StrictMode>,
);
};

const start = () => {
  reactRoot.render(<main className="loading-screen" aria-busy="true"><p role="status">Chargement de Frozen Save…</p></main>);
  void mount().catch((error: unknown) => {
    reactRoot.render(<I18nProvider><FederatedBootstrapFailure error={error} sharing={sharing}
      retry={start} navigate={path => window.location.assign(path)} /></I18nProvider>);
  });
};
start();
